/**
 * Admin entity edit page (CP17 Phase 2).
 *
 * Narrow first-slice scope (per FISHSTANK_V2_CHECKPOINT_17_FIRST_SLICE.md
 * §2 / §3.4):
 *   • Edits four columns only — primary_name, slug, kind, status.
 *   • Soft-merged rows render the form READ-ONLY with a banner pointing
 *     at the future merge flow. No PATCH issued from that state.
 *   • status='merged' is intentionally absent from the <select> options;
 *     the worker parser also rejects it as a hard guard.
 *   • Idempotency: one crypto.randomUUID() per form open, reused across
 *     retries so a double-click replays on mutation_events.idempotency_key
 *     UNIQUE without writing a second audit row.
 *   • Delta-only PATCH: the body sent contains ONLY the fields actually
 *     changed vs the loaded snapshot — the worker's whitelist-strict
 *     parser sees exactly what's intended to flip.
 *
 * Deferred to later CP17 phases or future checkpoints (intentionally
 * absent from this page):
 *   - alias / social-link editing
 *   - avatar / hero / portrait media
 *   - short_bio editing
 *   - merge / un-merge flow
 *   - delete flow
 *   - arc-participation editing
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import type {
  EntityAliasAdminItem,
  EntityAliasCreateRequest,
  EntityAliasDeleteReceipt,
  EntityAliasEditRequest,
  EntityAliasMutationReceipt,
  EntityAliasValidationError,
  EntityArcParticipationAdminItem,
  EntityEditRequest,
  EntityForAdmin,
  EntityKindAdmin,
  EntitySocialLinkAdminItem,
  EntitySocialLinkCreateRequest,
  EntitySocialLinkDeleteReceipt,
  EntitySocialLinkEditRequest,
  EntitySocialLinkMutationReceipt,
  EntitySocialLinkPlatform,
  EntitySocialLinkValidationError,
  EntityValidationError,
} from '@fishstank/contracts'
import { ENTITY_SOCIAL_LINK_PLATFORMS } from '@fishstank/contracts'

import { Shell } from '../layouts/Shell'
import {
  type EntityAliasMutationFetchResult,
  type EntityMutationFetchResult,
  type EntityPortraitUploadFetchResult,
  type EntitySocialLinkMutationFetchResult,
  deleteEntityAlias,
  deleteEntitySocialLink,
  fetchEntityAliasesForAdmin,
  fetchEntityForAdmin,
  fetchEntityParticipationsForAdmin,
  fetchEntitySocialLinksForAdmin,
  patchEntity,
  patchEntityAlias,
  patchEntitySocialLink,
  postEntityAlias,
  postEntitySocialLink,
  uploadEntityPortrait,
} from '../lib/worker'

// ────────────────────────────────────────────────────────────────────────────
// Local constants — locked to the DB enum so a renamed field never
// silently corrupts the form. The worker parser uses the same lists.
// ────────────────────────────────────────────────────────────────────────────

const KIND_OPTIONS: readonly EntityKindAdmin[] = [
  'fish',
  'freeloader',
  'host',
  'production',
  'other',
] as const

// 'merged' deliberately absent — the merge flow lives outside CP17.
const EDITABLE_STATUS_OPTIONS = ['active', 'retired', 'draft'] as const
type EditableStatus = (typeof EDITABLE_STATUS_OPTIONS)[number]

function newIdempotencyKey(): string {
  return globalThis.crypto.randomUUID()
}

function validationMessage(err: EntityValidationError): string {
  switch (err.code) {
    case 'invalid_uuid':
      return `Internal error: ${err.field} must be a UUID. Refresh and try again.`
    case 'invalid_enum':
      return `"${err.field}" is not allowed (expected one of: ${err.allowed.join(', ')}).`
    case 'no_editable_fields':
      return 'Nothing changed. Modify at least one field before submitting.'
    case 'primary_name_required':
      return 'Display name is required.'
    case 'primary_name_too_long':
      return `Display name is too long (max ${err.max} characters).`
    case 'invalid_slug':
      return 'Slug must be lowercase letters, numbers, and hyphens; must start with a letter or digit.'
    case 'field_too_long':
      return `"${err.field}" is too long (max ${err.max} characters).`
    case 'field_too_short':
      return `"${err.field}" is too short (min ${err.min} characters).`
    case 'slug_conflict':
      return 'Another entity already uses this slug.'
    case 'merged_status_not_editable':
      return 'Status "merged" is set by the merge flow, not by direct edit.'
    case 'idempotency_key_in_use':
      return 'This form was already submitted for a different entity. Refresh to start a new edit.'
    default:
      return 'Validation failed.'
  }
}

// ────────────────────────────────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────────────────────────────────

export function EntityEditPage(): JSX.Element {
  const params = useParams<{ id: string }>()
  const id = params.id ?? ''
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const idLooksValid = /^\d+$/.test(id)

  const query = useQuery({
    queryKey: ['entity', 'admin', id],
    queryFn: () => fetchEntityForAdmin(id),
    enabled: idLooksValid,
  })

  // Pinned per form-open. Stable across renders so React-Query cache
  // invalidations and re-renders don't accidentally flip the key.
  const idempotencyKey = useMemo(() => newIdempotencyKey(), [id])

  // Local edit state. Initialised lazily once the row loads.
  const [primaryName, setPrimaryName] = useState<string | null>(null)
  const [slug, setSlug] = useState<string | null>(null)
  const [kind, setKind] = useState<EntityKindAdmin | null>(null)
  const [status, setStatus] = useState<EditableStatus | null>(null)

  const [validationError, setValidationError] = useState<EntityValidationError | null>(null)
  const [networkError, setNetworkError] = useState<string | null>(null)
  const [receipt, setReceipt] = useState<EntityForAdmin | null>(null)

  const result = query.data
  const loaded = result && result.kind === 'ok' ? result.artifact.body.entity : null

  // First-render after load: seed local state.
  const seeded = primaryName !== null
  if (loaded && !seeded) {
    setPrimaryName(loaded.primary_name)
    setSlug(loaded.slug)
    setKind(loaded.kind)
    // The current row's status MAY be 'merged' (read-only render below);
    // if so, leave the editable status unset so the form's <select>
    // doesn't silently coerce it.
    if (loaded.status === 'active' || loaded.status === 'retired' || loaded.status === 'draft') {
      setStatus(loaded.status)
    }
  }

  const isMerged =
    loaded !== null && (loaded.status === 'merged' || loaded.merged_into_entity_id !== null)

  const buildDelta = (): EntityEditRequest | null => {
    if (!loaded) return null
    const body: EntityEditRequest = { idempotency_key: idempotencyKey }
    if (primaryName !== null && primaryName.trim() !== loaded.primary_name) {
      body.primary_name = primaryName.trim()
    }
    if (slug !== null && slug.trim() !== loaded.slug) {
      body.slug = slug.trim()
    }
    // Kind is editable post-0010 taxonomy realignment. Only
    // include it in the PATCH body when the operator actually
    // changed the value vs. the loaded snapshot — keeps audit
    // rows minimal and lets the worker's no_editable_fields
    // guard catch idempotency-key-only bodies.
    if (kind !== null && kind !== loaded.kind) {
      body.kind = kind
    }
    if (
      status !== null &&
      (loaded.status === 'active' || loaded.status === 'retired' || loaded.status === 'draft') &&
      status !== loaded.status
    ) {
      body.status = status
    }
    return body
  }

  const mutation = useMutation<EntityMutationFetchResult<unknown>, unknown, EntityEditRequest>({
    mutationFn: (body) => patchEntity(id, { body }),
    onSuccess: (res) => {
      setValidationError(null)
      setNetworkError(null)
      if (res.kind === 'ok') {
        // The receipt body shape is EntityMutationReceiptBody.
        const env = res.artifact as {
          body: { row: EntityForAdmin }
        }
        setReceipt(env.body.row)
        // Invalidate the row + the list so the next visit reflects the edit.
        queryClient.invalidateQueries({ queryKey: ['entity', 'admin', id] })
        queryClient.invalidateQueries({ queryKey: ['entities', 'admin', 'list'] })
      } else if (res.kind === 'validation_error') {
        setValidationError(res.error)
      } else if (res.kind === 'csrf_error') {
        setNetworkError(`CSRF: ${res.reason}`)
      } else if (res.kind === 'not_found') {
        setNetworkError('This entity was deleted before the edit landed.')
      } else {
        setNetworkError(res.message)
      }
    },
  })

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault()
    if (isMerged) return
    setValidationError(null)
    setNetworkError(null)
    const body = buildDelta()
    if (!body) return
    // Local empty-delta guard so we don't even round-trip a key-only body.
    const editableKeys = Object.keys(body).filter((k) => k !== 'idempotency_key')
    if (editableKeys.length === 0) {
      setValidationError({ code: 'no_editable_fields' })
      return
    }
    mutation.mutate(body)
  }

  return (
    <Shell
      provenance={
        result && (result.kind === 'ok' || result.kind === 'empty')
          ? result.artifact.provenance
          : null
      }
    >
      <header className="mb-6">
        <h1 className="text-2xl font-bold">Edit entity</h1>
        <p className="mt-1 text-sm text-slate-400">
          <Link to="/entities" className="text-fishstank-accent underline-offset-2 hover:underline">
            ← Back to entities
          </Link>
        </p>
      </header>

      {!idLooksValid && (
        <div
          data-testid="entity-edit-bad-id"
          className="rounded border border-rose-700 bg-rose-950/40 p-4 text-rose-100"
        >
          Invalid id in URL. Expected a positive integer.
        </div>
      )}

      {idLooksValid && query.isLoading && (
        <div data-testid="entity-edit-loading" className="text-slate-400">
          Loading entity…
        </div>
      )}

      {result && result.kind === 'error' && (
        <div
          data-testid="entity-edit-load-error"
          className="rounded border border-rose-700 bg-rose-950/40 p-4 text-rose-100"
        >
          {result.message}
        </div>
      )}

      {loaded && (
        <form data-testid="entity-edit-form" className="space-y-6" onSubmit={handleSubmit}>
          {isMerged && (
            <div
              data-testid="entity-edit-merged-banner"
              className="rounded border border-amber-700 bg-amber-950/40 p-4 text-amber-100"
            >
              This entity is soft-merged
              {loaded.merged_into_entity_id ? (
                <>
                  {' '}
                  into id <code>{loaded.merged_into_entity_id}</code>
                </>
              ) : null}
              . Edits are disabled in this slice — the merge / un-merge flow lands in a future
              checkpoint.
            </div>
          )}

          <div>
            <label
              htmlFor="entity-primary-name"
              className="mb-1 block text-sm font-medium text-slate-200"
            >
              Display name
            </label>
            <input
              id="entity-primary-name"
              data-testid="entity-edit-primary-name"
              type="text"
              className="w-full rounded border border-slate-700 bg-slate-900 px-3 py-2 text-slate-100"
              value={primaryName ?? ''}
              onChange={(e) => setPrimaryName(e.target.value)}
              disabled={isMerged || mutation.isPending}
              maxLength={500}
              required
            />
          </div>

          <div>
            <label htmlFor="entity-slug" className="mb-1 block text-sm font-medium text-slate-200">
              Slug
            </label>
            <input
              id="entity-slug"
              data-testid="entity-edit-slug"
              type="text"
              className="w-full rounded border border-slate-700 bg-slate-900 px-3 py-2 font-mono text-slate-100"
              value={slug ?? ''}
              onChange={(e) => setSlug(e.target.value)}
              disabled={isMerged || mutation.isPending}
              pattern="[a-z0-9][-a-z0-9]*"
              maxLength={120}
              required
            />
            <p className="mt-1 text-xs text-slate-500">
              Lowercase letters, digits, and hyphens; must start with a letter or digit.
            </p>
          </div>

          <div>
            <label htmlFor="entity-kind" className="mb-1 block text-sm font-medium text-slate-200">
              Kind
            </label>
            <select
              id="entity-kind"
              data-testid="entity-edit-kind"
              className="w-full rounded border border-slate-700 bg-slate-900 px-3 py-2 text-slate-100 disabled:cursor-not-allowed disabled:opacity-70"
              value={kind ?? ''}
              onChange={(e) => setKind(e.target.value as EntityKindAdmin)}
              disabled={isMerged || mutation.isPending}
              required
            >
              {KIND_OPTIONS.map((k) => (
                <option key={k} value={k}>
                  {k}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-slate-500">
              Categorizes the entity in the product taxonomy. Edit when correcting a misclassification.
            </p>
          </div>

          <div>
            <label
              htmlFor="entity-status"
              className="mb-1 block text-sm font-medium text-slate-200"
            >
              Status
            </label>
            <select
              id="entity-status"
              data-testid="entity-edit-status"
              className="w-full rounded border border-slate-700 bg-slate-900 px-3 py-2 text-slate-100"
              value={status ?? ''}
              onChange={(e) => setStatus(e.target.value as EditableStatus)}
              disabled={isMerged || mutation.isPending}
              required
            >
              {EDITABLE_STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-slate-500">
              "merged" is reserved for the merge flow and not editable here.
            </p>
          </div>

          {validationError && (
            <div
              data-testid="entity-edit-validation-error"
              className="rounded border border-rose-700 bg-rose-950/40 p-4 text-rose-100"
            >
              <strong>{validationError.code}</strong>
              <span className="ml-2">{validationMessage(validationError)}</span>
            </div>
          )}

          {networkError && (
            <div
              data-testid="entity-edit-network-error"
              className="rounded border border-rose-700 bg-rose-950/40 p-4 text-rose-100"
            >
              {networkError}
            </div>
          )}

          {receipt && (
            <div
              data-testid="entity-edit-receipt"
              className="rounded border border-emerald-700 bg-emerald-950/40 p-4 text-emerald-100"
            >
              Saved. Display name is now{' '}
              <strong data-testid="entity-edit-receipt-name">{receipt.primary_name}</strong>; slug
              is <code data-testid="entity-edit-receipt-slug">{receipt.slug}</code>; kind is{' '}
              <code data-testid="entity-edit-receipt-kind">{receipt.kind}</code>; status is{' '}
              <code data-testid="entity-edit-receipt-status">{receipt.status}</code>.
            </div>
          )}

          <div className="flex items-center gap-3">
            <button
              type="submit"
              data-testid="entity-edit-submit"
              className="rounded bg-fishstank-accent px-4 py-2 text-sm font-semibold text-slate-900 disabled:opacity-50"
              disabled={isMerged || mutation.isPending}
            >
              {mutation.isPending ? 'Saving…' : 'Save changes'}
            </button>
            <button
              type="button"
              data-testid="entity-edit-cancel"
              className="rounded border border-slate-700 px-4 py-2 text-sm text-slate-200"
              onClick={() => navigate('/entities')}
              disabled={mutation.isPending}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {loaded && idLooksValid && !isMerged && (
        <EntityPortraitSection
          entityId={id}
          entity={loaded}
          onAttached={() => {
            queryClient.invalidateQueries({ queryKey: ['entity', 'admin', id] })
            queryClient.invalidateQueries({ queryKey: ['entities', 'admin', 'list'] })
          }}
        />
      )}

      {loaded && idLooksValid && !isMerged && (
        <EntityAliasesSection entityId={id} />
      )}

      {loaded && idLooksValid && !isMerged && (
        <EntitySocialLinksSection entityId={id} />
      )}

      {loaded && idLooksValid && <EntityParticipationsSection entityId={id} />}
    </Shell>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// EntityPortraitSection (Slice 1 of entity-portraits)
//
// Two-step upload-then-attach flow. Picking a file triggers the
// multipart POST to the worker's portrait route; on success we PATCH
// the entity with the returned `media_asset_id`. "Clear" sends a
// PATCH with `avatar_asset_id: null`.
//
// Width/height are extracted client-side via Image() before the
// multipart post — the worker requires them and we don't want to
// upload bytes only to be rejected on a missing dimension.
//
// Hidden when the entity is soft-merged (parent gates on isMerged).
// ────────────────────────────────────────────────────────────────────────────

interface EntityPortraitSectionProps {
  entityId: string
  entity: EntityForAdmin
  onAttached: () => void
}

const PORTRAIT_ACCEPT = 'image/png,image/jpeg,image/webp'
const PORTRAIT_MAX_BYTES_DISPLAY = '5 MB'

function portraitErrorMessage(err: {
  code: string
  field?: string
  message?: string
}): string {
  switch (err.code) {
    case 'invalid_mime':
      return 'Unsupported image format. Use PNG, JPEG, or WebP.'
    case 'file_too_large':
      return `File too large. Max is ${PORTRAIT_MAX_BYTES_DISPLAY}.`
    case 'empty_upload':
      return 'The selected file is empty.'
    case 'invalid_dimension':
      return 'Could not read the image dimensions. Try a different file.'
    case 'file_required':
      return 'No file selected.'
    case 'entity_not_found':
      return 'This entity no longer exists.'
    case 'r2_write_failed':
    case 'media_assets_write_failed':
      return `Server error during upload (${err.code}). Try again.`
    default:
      return err.message ?? `Upload failed (${err.code}).`
  }
}

async function readImageDimensions(
  file: File,
): Promise<{ width: number; height: number }> {
  const url = URL.createObjectURL(file)
  try {
    const img = new Image()
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve()
      img.onerror = () => reject(new Error('image_load_failed'))
      img.src = url
    })
    return { width: img.naturalWidth, height: img.naturalHeight }
  } finally {
    URL.revokeObjectURL(url)
  }
}

function EntityPortraitSection(
  props: EntityPortraitSectionProps,
): JSX.Element {
  const { entityId, entity, onAttached } = props
  const queryClient = useQueryClient()
  const [networkError, setNetworkError] = useState<string | null>(null)
  const [validationMsg, setValidationMsg] = useState<string | null>(null)
  const [isBusy, setIsBusy] = useState(false)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  // The current portrait URL (if any) is on the parent's entity row
  // via the public artifact's `portrait.storage_key` — but the admin
  // detail view doesn't include `portrait`. Reconstruct from
  // avatar_asset_id presence + a known URL pattern; if absent, we
  // show the empty-state. The Slice 1 plan accepts this looseness;
  // a future slice can add a dedicated admin GET that includes the
  // portrait URL.
  const hasAttached = entity.avatar_asset_id !== null
  const displayUrl = previewUrl

  async function handleFilePick(file: File): Promise<void> {
    setNetworkError(null)
    setValidationMsg(null)
    setIsBusy(true)
    try {
      let dimensions: { width: number; height: number }
      try {
        dimensions = await readImageDimensions(file)
      } catch {
        setValidationMsg(
          'Could not read the image. Use a PNG, JPEG, or WebP file.',
        )
        return
      }

      const result: EntityPortraitUploadFetchResult =
        await uploadEntityPortrait(entityId, file, {
          width: dimensions.width,
          height: dimensions.height,
        })

      if (result.kind === 'ok') {
        const receipt = result.artifact.body
        // Step 2: attach the new asset via the existing PATCH route.
        const patch = await patchEntity(entityId, {
          body: {
            idempotency_key: globalThis.crypto.randomUUID(),
            avatar_asset_id: receipt.media_asset_id,
          },
        })
        if (patch.kind === 'ok') {
          setPreviewUrl(receipt.url)
          onAttached()
        } else if (patch.kind === 'validation_error') {
          // Narrowing: the discriminated union has one variant
          // (`no_editable_fields`) without a `field` — guard before
          // surfacing it.
          const field =
            'field' in patch.error ? patch.error.field : undefined
          setValidationMsg(
            `Attach failed: ${patch.error.code}` +
              (field !== undefined ? ` (${field})` : ''),
          )
        } else if (patch.kind === 'csrf_error') {
          setNetworkError(`CSRF: ${patch.reason}`)
        } else if (patch.kind === 'not_found') {
          setNetworkError('Entity disappeared between upload and attach.')
        } else {
          setNetworkError(patch.message)
        }
      } else if (result.kind === 'validation_error') {
        setValidationMsg(portraitErrorMessage(result.error))
      } else if (result.kind === 'csrf_error') {
        setNetworkError(`CSRF: ${result.reason}`)
      } else if (result.kind === 'not_found') {
        setNetworkError('This entity no longer exists.')
      } else {
        setNetworkError(result.message)
      }
    } finally {
      setIsBusy(false)
    }
  }

  async function handleClear(): Promise<void> {
    setNetworkError(null)
    setValidationMsg(null)
    setIsBusy(true)
    try {
      const patch = await patchEntity(entityId, {
        body: {
          idempotency_key: globalThis.crypto.randomUUID(),
          avatar_asset_id: null,
        },
      })
      if (patch.kind === 'ok') {
        setPreviewUrl(null)
        onAttached()
        queryClient.invalidateQueries({
          queryKey: ['entity', 'admin', entityId],
        })
      } else if (patch.kind === 'validation_error') {
        setValidationMsg(`Clear failed: ${patch.error.code}`)
      } else if (patch.kind === 'csrf_error') {
        setNetworkError(`CSRF: ${patch.reason}`)
      } else if (patch.kind === 'not_found') {
        setNetworkError('This entity no longer exists.')
      } else {
        setNetworkError(patch.message)
      }
    } finally {
      setIsBusy(false)
    }
  }

  return (
    <section
      data-testid="entity-portrait-section"
      data-entity-id={entityId}
      data-portrait-attached={hasAttached ? 'true' : 'false'}
      className="mt-10"
    >
      <h2 className="mb-3 text-lg font-semibold text-slate-100">Portrait</h2>

      <div className="flex flex-col gap-4 rounded-md border border-slate-800 bg-fishstank-surface p-4 sm:flex-row sm:items-start">
        <div
          data-testid="entity-portrait-preview"
          className="flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-full border border-slate-700 bg-slate-900 text-xs text-slate-500"
        >
          {displayUrl ? (
            <img
              src={displayUrl}
              alt={`${entity.primary_name} portrait`}
              className="h-full w-full object-cover"
            />
          ) : hasAttached ? (
            <span data-testid="entity-portrait-attached-no-preview">
              attached
            </span>
          ) : (
            <span data-testid="entity-portrait-empty">no image</span>
          )}
        </div>

        <div className="flex-1 space-y-3">
          <p className="text-xs text-slate-400">
            PNG, JPEG, or WebP. Max {PORTRAIT_MAX_BYTES_DISPLAY}. Upload
            replaces any existing portrait for this entity.
          </p>

          <div className="flex flex-wrap items-center gap-3">
            <label
              data-testid="entity-portrait-file-label"
              className="inline-flex cursor-pointer items-center rounded border border-fishstank-accent px-3 py-1.5 text-xs font-semibold text-fishstank-accent hover:bg-fishstank-accent/10"
            >
              {hasAttached ? 'Replace' : 'Upload'}
              <input
                data-testid="entity-portrait-file-input"
                type="file"
                accept={PORTRAIT_ACCEPT}
                disabled={isBusy}
                className="sr-only"
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) {
                    void handleFilePick(file)
                    // Reset the input so re-picking the same file fires
                    // change again.
                    e.target.value = ''
                  }
                }}
              />
            </label>

            {hasAttached && (
              <button
                type="button"
                data-testid="entity-portrait-clear"
                className="rounded border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:border-rose-700 hover:text-rose-200 disabled:opacity-50"
                disabled={isBusy}
                onClick={() => void handleClear()}
              >
                Clear
              </button>
            )}

            {isBusy && (
              <span
                data-testid="entity-portrait-busy"
                className="text-xs text-slate-500"
              >
                Uploading…
              </span>
            )}
          </div>

          {validationMsg && (
            <p
              data-testid="entity-portrait-validation-error"
              className="text-xs text-rose-200"
            >
              {validationMsg}
            </p>
          )}
          {networkError && (
            <p
              data-testid="entity-portrait-network-error"
              className="text-xs text-rose-200"
            >
              {networkError}
            </p>
          )}
        </div>
      </div>
    </section>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// EntityParticipationsSection (Refinement Phase B4)
//
// Read-only section rendered below the entity-edit form. Lists the
// arcs this entity participates in, sorted by `arc.display_order ASC
// NULLS LAST` then `LOWER(arc.title) ASC` (matched in the loader).
//
// Behavior:
//   - winner status surfaces as a quiet badge (no sort priority)
//   - each row carries an "Edit on arc page" link to
//     /arcs/${arc_id}/edit#participation-${id} so the operator can
//     drill into the existing B3 PATCH/DELETE controls
//   - empty state: plain factual copy
//
// Out of B4 scope: any mutation (edits/deletes remain on ArcEdit).
// ────────────────────────────────────────────────────────────────────────────

interface EntityParticipationsSectionProps {
  entityId: string
}

function EntityParticipationsSection(
  props: EntityParticipationsSectionProps,
): JSX.Element {
  const { entityId } = props
  const query = useQuery({
    queryKey: ['entity-participations', entityId],
    queryFn: () => fetchEntityParticipationsForAdmin(entityId),
    enabled: /^\d+$/.test(entityId),
  })

  const result = query.data

  return (
    <section
      className="mt-10"
      data-testid={`entity-participations-section-${entityId}`}
    >
      <h2 className="mb-3 text-lg font-semibold text-slate-100">Participations</h2>

      {query.isLoading && (
        <div
          data-testid={`entity-participations-loading-${entityId}`}
          className="text-sm text-slate-400"
        >
          Loading participations…
        </div>
      )}

      {result && result.kind === 'error' && (
        <div
          data-testid={`entity-participations-error-${entityId}`}
          className="rounded border border-rose-700 bg-rose-950/40 p-3 text-sm text-rose-100"
        >
          {result.message}
        </div>
      )}

      {result && (result.kind === 'ok' || result.kind === 'empty') && (
        <EntityParticipationsTable
          entityId={entityId}
          rows={result.artifact.body.participations}
        />
      )}
    </section>
  )
}

interface EntityParticipationsTableProps {
  entityId: string
  rows: readonly EntityArcParticipationAdminItem[]
}

function EntityParticipationsTable(
  props: EntityParticipationsTableProps,
): JSX.Element {
  const { entityId, rows } = props
  if (rows.length === 0) {
    return (
      <p
        data-testid={`entity-participations-empty-${entityId}`}
        className="text-sm text-slate-400"
      >
        This entity isn't in any arcs yet. Link it to one from the arc admin
        page.
      </p>
    )
  }
  return (
    <div className="overflow-x-auto">
      <table
        data-testid={`entity-participations-table-${entityId}`}
        className="min-w-full divide-y divide-slate-700 border border-slate-800 text-xs"
      >
        <thead className="bg-slate-900 text-slate-300">
          <tr>
            <th className="px-3 py-2 text-left font-medium">Arc</th>
            <th className="px-3 py-2 text-left font-medium">Role</th>
            <th
              className="px-3 py-2 text-left font-medium"
              title="Per-arc category override (migration 0013). Empty = inherits this entity's kind from the page header."
            >
              Category override
            </th>
            <th className="px-3 py-2 text-left font-medium">Status</th>
            <th className="px-3 py-2 text-left font-medium">Joined</th>
            <th className="px-3 py-2 text-left font-medium">Left</th>
            <th className="px-3 py-2 text-left font-medium">Notes</th>
            <th className="px-3 py-2 text-left font-medium">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-800">
          {rows.map((row) => (
            <EntityParticipationRow key={row.id} row={row} />
          ))}
        </tbody>
      </table>
    </div>
  )
}

interface EntityParticipationRowProps {
  row: EntityArcParticipationAdminItem
}

function EntityParticipationRow(
  props: EntityParticipationRowProps,
): JSX.Element {
  const { row } = props
  const editHref = `/arcs/${row.arc_id}/edit#participation-${row.id}`
  return (
    <tr
      data-testid={`entity-participation-row-${row.id}`}
      data-arc-slug={row.arc.slug}
      className="text-slate-200"
    >
      <td className="px-3 py-2 align-top">
        <Link
          to={editHref}
          className="text-fishstank-accent underline-offset-2 hover:underline"
        >
          {row.arc.title}
        </Link>
        <div className="mt-0.5 text-[10px] text-slate-500">
          <code className="text-slate-400">{row.arc.slug}</code>
          <span className="ml-1.5">{row.arc.status}</span>
          {row.arc.display_order !== null && (
            <span className="ml-1.5">· #{row.arc.display_order}</span>
          )}
        </div>
      </td>
      <td className="px-3 py-2 align-top">{row.role}</td>
      <td
        className="px-3 py-2 align-top"
        data-testid={`entity-participation-row-kind-override-${row.id}`}
        data-kind-override={row.kind_override ?? ''}
      >
        {row.kind_override === null ? (
          <span className="text-slate-500">—</span>
        ) : (
          <span className="rounded bg-indigo-900/60 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-indigo-200">
            {row.kind_override}
          </span>
        )}
      </td>
      <td className="px-3 py-2 align-top">
        <span>{row.status}</span>
        {row.status === 'winner' && (
          <span
            data-testid={`entity-participation-winner-badge-${row.id}`}
            className="ml-1 text-[10px] text-yellow-400"
          >
            ★ winner
          </span>
        )}
      </td>
      <td className="px-3 py-2 align-top text-slate-400">
        {row.joined_at ? row.joined_at.slice(0, 10) : '—'}
      </td>
      <td className="px-3 py-2 align-top text-slate-400">
        {row.left_at ? row.left_at.slice(0, 10) : '—'}
      </td>
      <td className="px-3 py-2 align-top text-slate-400" title={row.notes ?? ''}>
        {row.notes
          ? row.notes.length > 60
            ? `${row.notes.slice(0, 60)}…`
            : row.notes
          : '—'}
      </td>
      <td className="px-3 py-2 align-top">
        <Link
          to={editHref}
          className="text-fishstank-accent underline-offset-2 hover:underline"
        >
          Edit on arc page
        </Link>
      </td>
    </tr>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// EntityAliasesSection (Refinement Phase D)
//
// Read + create + edit + delete admin surface for entity_aliases.
// Mirrors B3's <ParticipationViewRow> / <EditParticipationRow>
// mode-state-machine pattern.
//
// Hidden entirely when the entity is soft-merged (the parent page
// gates this on `isMerged`).
//
// Server-side normalization: clients send raw `alias`; the worker
// computes `normalized_alias` via `lib/alias-normalize.ts:normalizeAlias`.
// The admin row exposes both — `normalized_alias` is read-only on
// the wire and rendered as a non-editable column.
// ────────────────────────────────────────────────────────────────────────────

interface EntityAliasesSectionProps {
  entityId: string
}

function newAliasIdempotencyKey(): string {
  return crypto.randomUUID()
}

function aliasValidationMessage(err: EntityAliasValidationError): string {
  switch (err.code) {
    case 'invalid_body':
      return 'Invalid request body.'
    case 'entity_id_required':
      return 'entity_id is required.'
    case 'invalid_entity_id':
      return 'entity_id must be a positive integer.'
    case 'unknown_entity_id':
      return 'Entity not found.'
    case 'entity_soft_merged':
      return 'This entity is soft-merged; aliases cannot be added.'
    case 'alias_required':
      return 'Alias text is required.'
    case 'alias_too_long':
      return `Alias is too long (max ${err.max} characters).`
    case 'empty_normalized_alias':
      return 'This alias normalizes to empty (e.g. punctuation- or whitespace-only). Use letters or digits.'
    case 'alias_already_exists':
      return 'This entity already has an alias with the same normalized form.'
    case 'alias_already_primary_elsewhere':
      return 'Another entity already claims this alias as its primary form.'
    case 'source_too_long':
      return `Source is too long (max ${err.max} characters).`
    case 'invalid_uuid':
      return 'Invalid idempotency key.'
    case 'idempotency_key_in_use':
      return 'This idempotency key has already been used for a different action.'
    case 'no_editable_fields':
      return 'No editable fields supplied.'
    case 'alias_not_found':
      return 'This alias no longer exists.'
  }
}

function EntityAliasesSection(props: EntityAliasesSectionProps): JSX.Element {
  const { entityId } = props
  const query = useQuery({
    queryKey: ['entity-aliases', entityId],
    queryFn: () => fetchEntityAliasesForAdmin(entityId),
    enabled: /^\d+$/.test(entityId),
  })
  const result = query.data

  return (
    <section
      className="mt-10"
      data-testid={`entity-aliases-section-${entityId}`}
    >
      <h2 className="mb-3 text-lg font-semibold text-slate-100">Aliases</h2>

      {query.isLoading && (
        <div
          data-testid={`entity-aliases-loading-${entityId}`}
          className="text-sm text-slate-400"
        >
          Loading aliases…
        </div>
      )}

      {result && result.kind === 'error' && (
        <div
          data-testid={`entity-aliases-error-${entityId}`}
          className="rounded border border-rose-700 bg-rose-950/40 p-3 text-sm text-rose-100"
        >
          {result.message}
        </div>
      )}

      {result && (result.kind === 'ok' || result.kind === 'empty') && (
        <EntityAliasesTable
          entityId={entityId}
          rows={result.artifact.body.aliases}
        />
      )}

      {result && (result.kind === 'ok' || result.kind === 'empty') && (
        <AddAliasForm entityId={entityId} />
      )}
    </section>
  )
}

interface EntityAliasesTableProps {
  entityId: string
  rows: readonly EntityAliasAdminItem[]
}

function EntityAliasesTable(props: EntityAliasesTableProps): JSX.Element {
  const { entityId, rows } = props
  if (rows.length === 0) {
    return (
      <p
        data-testid={`entity-aliases-empty-${entityId}`}
        className="text-sm text-slate-400"
      >
        This entity has no aliases recorded.
      </p>
    )
  }
  return (
    <div className="overflow-x-auto">
      <table
        data-testid={`entity-aliases-table-${entityId}`}
        className="min-w-full divide-y divide-slate-700 border border-slate-800 text-xs"
      >
        <thead className="bg-slate-900 text-slate-300">
          <tr>
            <th className="px-3 py-2 text-left font-medium">Alias</th>
            <th className="px-3 py-2 text-left font-medium">Normalized</th>
            <th className="px-3 py-2 text-left font-medium">Primary</th>
            <th className="px-3 py-2 text-left font-medium">Source</th>
            <th className="px-3 py-2 text-left font-medium">Created</th>
            <th className="px-3 py-2 text-left font-medium">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-800">
          {rows.map((row) => (
            <AliasRow key={row.id} entityId={entityId} row={row} />
          ))}
        </tbody>
      </table>
    </div>
  )
}

type AliasRowMode = 'view' | 'edit' | 'confirm-delete'

interface AliasRowProps {
  entityId: string
  row: EntityAliasAdminItem
}

function AliasRow(props: AliasRowProps): JSX.Element {
  const [mode, setMode] = useState<AliasRowMode>('view')
  if (mode === 'edit') {
    return (
      <EditAliasRow
        entityId={props.entityId}
        row={props.row}
        onSaved={() => setMode('view')}
        onCancel={() => setMode('view')}
      />
    )
  }
  return (
    <ViewAliasRow
      entityId={props.entityId}
      row={props.row}
      mode={mode}
      onEdit={() => setMode('edit')}
      onAskDelete={() => setMode('confirm-delete')}
      onCancelDelete={() => setMode('view')}
      onDeleted={() => setMode('view')}
    />
  )
}

interface ViewAliasRowProps {
  entityId: string
  row: EntityAliasAdminItem
  mode: 'view' | 'confirm-delete'
  onEdit: () => void
  onAskDelete: () => void
  onCancelDelete: () => void
  onDeleted: () => void
}

function ViewAliasRow(props: ViewAliasRowProps): JSX.Element {
  const { entityId, row, mode, onEdit, onAskDelete, onCancelDelete, onDeleted } = props
  const queryClient = useQueryClient()
  const [networkError, setNetworkError] = useState<string | null>(null)
  const idempotencyKey = useMemo(() => newAliasIdempotencyKey(), [row.id, mode])

  const mutation = useMutation<
    EntityAliasMutationFetchResult<EntityAliasDeleteReceipt>,
    unknown,
    void
  >({
    mutationFn: () =>
      deleteEntityAlias(row.id, { body: { idempotency_key: idempotencyKey } }),
    onSuccess: (res) => {
      setNetworkError(null)
      if (res.kind === 'ok') {
        queryClient.invalidateQueries({
          queryKey: ['entity-aliases', entityId],
        })
        onDeleted()
      } else if (res.kind === 'csrf_error') {
        setNetworkError(`CSRF: ${res.reason}`)
      } else if (res.kind === 'not_found') {
        // Treat as already-deleted.
        queryClient.invalidateQueries({
          queryKey: ['entity-aliases', entityId],
        })
        onDeleted()
      } else if (res.kind === 'validation_error') {
        setNetworkError(aliasValidationMessage(res.error))
      } else {
        setNetworkError(res.message)
      }
    },
  })

  return (
    <tr
      data-testid={`entity-alias-row-${row.id}`}
      data-alias-normalized={row.normalized_alias}
      className="text-slate-200"
    >
      <td className="px-3 py-2 align-top font-medium">{row.alias}</td>
      <td className="px-3 py-2 align-top text-slate-400">
        <code>{row.normalized_alias}</code>
      </td>
      <td className="px-3 py-2 align-top">
        {row.is_primary ? (
          <span
            data-testid={`entity-alias-primary-badge-${row.id}`}
            className="text-yellow-400"
          >
            ★ primary
          </span>
        ) : (
          <span className="text-slate-500">—</span>
        )}
      </td>
      <td className="px-3 py-2 align-top text-slate-400">
        {row.source ?? '—'}
      </td>
      <td className="px-3 py-2 align-top text-slate-400">
        {row.created_at.slice(0, 10)}
      </td>
      <td className="px-3 py-2 align-top">
        {mode === 'view' && (
          <div className="flex gap-2">
            <button
              type="button"
              data-testid={`entity-alias-edit-button-${row.id}`}
              className="rounded border border-slate-700 px-2 py-1 text-xs text-slate-200"
              onClick={onEdit}
            >
              Edit
            </button>
            <button
              type="button"
              data-testid={`entity-alias-delete-button-${row.id}`}
              className="rounded border border-rose-700 px-2 py-1 text-xs text-rose-200"
              onClick={onAskDelete}
            >
              Delete
            </button>
          </div>
        )}
        {mode === 'confirm-delete' && (
          <div
            data-testid={`entity-alias-confirm-delete-${row.id}`}
            className="flex flex-col gap-1"
          >
            <span className="text-xs text-rose-200">Confirm delete?</span>
            <div className="flex gap-2">
              <button
                type="button"
                data-testid={`entity-alias-confirm-delete-yes-${row.id}`}
                className="rounded border border-rose-700 bg-rose-900/40 px-2 py-1 text-xs text-rose-100"
                onClick={() => mutation.mutate()}
                disabled={mutation.isPending}
              >
                {mutation.isPending ? 'Deleting…' : 'Yes, delete'}
              </button>
              <button
                type="button"
                data-testid={`entity-alias-confirm-delete-cancel-${row.id}`}
                className="rounded border border-slate-700 px-2 py-1 text-xs text-slate-200"
                onClick={onCancelDelete}
                disabled={mutation.isPending}
              >
                Cancel
              </button>
            </div>
            {networkError && (
              <p
                data-testid={`entity-alias-row-error-${row.id}`}
                className="mt-1 text-xs text-rose-200"
              >
                {networkError}
              </p>
            )}
          </div>
        )}
      </td>
    </tr>
  )
}

interface EditAliasRowProps {
  entityId: string
  row: EntityAliasAdminItem
  onSaved: () => void
  onCancel: () => void
}

function EditAliasRow(props: EditAliasRowProps): JSX.Element {
  const { entityId, row, onSaved, onCancel } = props
  const queryClient = useQueryClient()
  const [alias, setAlias] = useState<string>(row.alias)
  const [isPrimary, setIsPrimary] = useState<boolean>(row.is_primary)
  const [source, setSource] = useState<string>(row.source ?? '')
  const [validationError, setValidationError] =
    useState<EntityAliasValidationError | null>(null)
  const [networkError, setNetworkError] = useState<string | null>(null)
  const idempotencyKey = useMemo(() => newAliasIdempotencyKey(), [row.id])

  const mutation = useMutation<
    EntityAliasMutationFetchResult<EntityAliasMutationReceipt>,
    unknown,
    EntityAliasEditRequest
  >({
    mutationFn: (body) => patchEntityAlias(row.id, { body }),
    onSuccess: (res) => {
      setValidationError(null)
      setNetworkError(null)
      if (res.kind === 'ok') {
        queryClient.invalidateQueries({
          queryKey: ['entity-aliases', entityId],
        })
        onSaved()
      } else if (res.kind === 'validation_error') {
        setValidationError(res.error)
      } else if (res.kind === 'csrf_error') {
        setNetworkError(`CSRF: ${res.reason}`)
      } else if (res.kind === 'not_found') {
        setNetworkError('This alias no longer exists.')
      } else {
        setNetworkError(res.message)
      }
    },
  })

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault()
    const body: EntityAliasEditRequest = { idempotency_key: idempotencyKey }
    if (alias !== row.alias) body.alias = alias
    if (isPrimary !== row.is_primary) body.is_primary = isPrimary
    const newSource = source.trim() === '' ? null : source.trim()
    if (newSource !== row.source) body.source = newSource
    const editableKeys = Object.keys(body).filter((k) => k !== 'idempotency_key')
    if (editableKeys.length === 0) {
      setValidationError({ code: 'no_editable_fields' })
      return
    }
    mutation.mutate(body)
  }

  return (
    <tr
      data-testid={`entity-alias-edit-row-${row.id}`}
      data-alias-normalized={row.normalized_alias}
    >
      <td className="px-3 py-2">
        <input
          data-testid={`entity-alias-edit-alias-${row.id}`}
          type="text"
          className="w-full rounded border border-slate-700 bg-slate-900 px-2 py-1 text-xs text-slate-100"
          value={alias}
          onChange={(e) => setAlias(e.target.value)}
          maxLength={200}
          disabled={mutation.isPending}
        />
      </td>
      <td className="px-3 py-2 text-slate-500">
        <span className="text-[10px]">re-normalized server-side</span>
      </td>
      <td className="px-3 py-2">
        <label className="inline-flex items-center gap-1 text-xs text-slate-200">
          <input
            data-testid={`entity-alias-edit-is-primary-${row.id}`}
            type="checkbox"
            checked={isPrimary}
            onChange={(e) => setIsPrimary(e.target.checked)}
            disabled={mutation.isPending}
          />
          primary
        </label>
      </td>
      <td className="px-3 py-2">
        <input
          data-testid={`entity-alias-edit-source-${row.id}`}
          type="text"
          className="w-full rounded border border-slate-700 bg-slate-900 px-2 py-1 text-xs text-slate-100"
          value={source}
          onChange={(e) => setSource(e.target.value)}
          maxLength={100}
          disabled={mutation.isPending}
          placeholder="optional"
        />
      </td>
      <td className="px-3 py-2 text-slate-500">{row.created_at.slice(0, 10)}</td>
      <td className="px-3 py-2 align-top">
        {validationError && (
          <p
            data-testid={`entity-alias-edit-error-${row.id}`}
            className="mb-1 text-xs text-rose-200"
          >
            {aliasValidationMessage(validationError)}
          </p>
        )}
        {networkError && (
          <p
            data-testid={`entity-alias-edit-network-error-${row.id}`}
            className="mb-1 text-xs text-rose-200"
          >
            {networkError}
          </p>
        )}
        <form
          data-testid={`entity-alias-edit-form-${row.id}`}
          className="flex gap-2"
          onSubmit={handleSubmit}
        >
          <button
            type="submit"
            data-testid={`entity-alias-edit-submit-${row.id}`}
            className="rounded border border-fishstank-accent px-2 py-1 text-xs text-fishstank-accent"
            disabled={mutation.isPending}
          >
            {mutation.isPending ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            data-testid={`entity-alias-edit-cancel-${row.id}`}
            className="rounded border border-slate-700 px-2 py-1 text-xs text-slate-200"
            onClick={onCancel}
            disabled={mutation.isPending}
          >
            Cancel
          </button>
        </form>
      </td>
    </tr>
  )
}

interface AddAliasFormProps {
  entityId: string
}

function AddAliasForm(props: AddAliasFormProps): JSX.Element {
  const { entityId } = props
  const queryClient = useQueryClient()
  const [alias, setAlias] = useState<string>('')
  const [isPrimary, setIsPrimary] = useState<boolean>(false)
  const [source, setSource] = useState<string>('')
  const [validationError, setValidationError] =
    useState<EntityAliasValidationError | null>(null)
  const [networkError, setNetworkError] = useState<string | null>(null)
  const [recentlyAdded, setRecentlyAdded] = useState<string | null>(null)
  const [idempotencyKey, setIdempotencyKey] = useState<string>(() =>
    newAliasIdempotencyKey(),
  )

  const mutation = useMutation<
    EntityAliasMutationFetchResult<EntityAliasMutationReceipt>,
    unknown,
    EntityAliasCreateRequest
  >({
    mutationFn: (body) => postEntityAlias({ body }),
    onSuccess: (res) => {
      setValidationError(null)
      setNetworkError(null)
      if (res.kind === 'ok') {
        setRecentlyAdded(res.artifact.body.row.alias)
        queryClient.invalidateQueries({
          queryKey: ['entity-aliases', entityId],
        })
        // Clear form + rotate idempotency key for the next add.
        setAlias('')
        setIsPrimary(false)
        setSource('')
        setIdempotencyKey(newAliasIdempotencyKey())
      } else if (res.kind === 'validation_error') {
        setValidationError(res.error)
      } else if (res.kind === 'csrf_error') {
        setNetworkError(`CSRF: ${res.reason}`)
      } else if (res.kind === 'not_found') {
        setNetworkError('This entity no longer exists.')
      } else {
        setNetworkError(res.message)
      }
    },
  })

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault()
    const body: EntityAliasCreateRequest = {
      entity_id: entityId,
      alias,
      idempotency_key: idempotencyKey,
    }
    if (isPrimary) body.is_primary = true
    const trimmedSource = source.trim()
    if (trimmedSource.length > 0) body.source = trimmedSource
    mutation.mutate(body)
  }

  return (
    <form
      data-testid={`entity-alias-add-form-${entityId}`}
      className="mt-3 flex flex-col gap-2 rounded border border-slate-800 p-3 text-xs"
      onSubmit={handleSubmit}
    >
      <div className="flex flex-wrap items-end gap-2">
        <label className="flex flex-col">
          <span className="mb-1 text-slate-300">Alias</span>
          <input
            data-testid={`entity-alias-add-alias-${entityId}`}
            type="text"
            className="rounded border border-slate-700 bg-slate-900 px-2 py-1 text-slate-100"
            value={alias}
            onChange={(e) => setAlias(e.target.value)}
            maxLength={200}
            required
            disabled={mutation.isPending}
          />
        </label>
        <label className="flex items-center gap-1 text-slate-200">
          <input
            data-testid={`entity-alias-add-is-primary-${entityId}`}
            type="checkbox"
            checked={isPrimary}
            onChange={(e) => setIsPrimary(e.target.checked)}
            disabled={mutation.isPending}
          />
          primary
        </label>
        <label className="flex flex-col">
          <span className="mb-1 text-slate-300">Source (optional)</span>
          <input
            data-testid={`entity-alias-add-source-${entityId}`}
            type="text"
            className="rounded border border-slate-700 bg-slate-900 px-2 py-1 text-slate-100"
            value={source}
            onChange={(e) => setSource(e.target.value)}
            maxLength={100}
            placeholder="admin"
            disabled={mutation.isPending}
          />
        </label>
        <button
          type="submit"
          data-testid={`entity-alias-add-submit-${entityId}`}
          className="rounded border border-fishstank-accent px-3 py-1 text-fishstank-accent"
          disabled={mutation.isPending}
        >
          {mutation.isPending ? 'Adding…' : 'Add alias'}
        </button>
      </div>

      {validationError && (
        <p
          data-testid={`entity-alias-add-error-${entityId}`}
          className="text-rose-200"
        >
          {aliasValidationMessage(validationError)}
        </p>
      )}
      {networkError && (
        <p
          data-testid={`entity-alias-add-network-error-${entityId}`}
          className="text-rose-200"
        >
          {networkError}
        </p>
      )}
      {recentlyAdded && (
        <p
          data-testid={`entity-alias-add-receipt-${entityId}`}
          className="text-emerald-200"
        >
          Added alias <strong>{recentlyAdded}</strong>.
        </p>
      )}
    </form>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// EntitySocialLinksSection
//
// Read + create + edit + delete admin surface for entity_social_links.
// Mirrors EntityAliasesSection one-for-one. Hidden on soft-merged
// entities (gated by parent on `isMerged`).
//
// UI shape:
//   - Read-only table with one row per existing link
//   - Add form below: platform <select> + handle text + url text +
//     submit button
//   - Per-row Edit / Delete affordances (mode state machine)
//   - (entity_id, platform) is UNIQUE — the add form's platform
//     options are not pre-filtered against existing rows; the worker
//     returns `platform_already_exists` and we surface it inline.
//     Operator can then pick a different platform or edit the
//     existing row.
// ────────────────────────────────────────────────────────────────────────────

interface EntitySocialLinksSectionProps {
  entityId: string
}

function newSocialLinkIdempotencyKey(): string {
  return crypto.randomUUID()
}

function socialLinkValidationMessage(
  err: EntitySocialLinkValidationError,
): string {
  switch (err.code) {
    case 'invalid_body':
      return 'Invalid request body.'
    case 'entity_id_required':
      return 'entity_id is required.'
    case 'invalid_entity_id':
      return 'entity_id must be a positive integer.'
    case 'unknown_entity_id':
      return 'Entity not found.'
    case 'entity_soft_merged':
      return 'This entity is soft-merged; social links cannot be added.'
    case 'platform_required':
      return 'Pick a platform.'
    case 'invalid_platform':
      return `Platform must be one of: ${err.allowed.join(', ')}.`
    case 'platform_already_exists':
      return 'This entity already has a link for that platform. Edit the existing row, or delete it first.'
    case 'handle_required':
      return 'Handle is required (e.g. "@username" or your channel name).'
    case 'handle_too_long':
      return `Handle is too long (max ${err.max} characters).`
    case 'invalid_url':
      return 'URL must be a valid http(s):// link, or leave it blank.'
    case 'url_too_long':
      return `URL is too long (max ${err.max} characters).`
    case 'invalid_uuid':
      return 'Invalid idempotency key.'
    case 'idempotency_key_in_use':
      return 'This idempotency key has already been used for a different action.'
    case 'no_editable_fields':
      return 'No editable fields supplied.'
    case 'social_link_not_found':
      return 'This social link no longer exists.'
  }
}

function EntitySocialLinksSection(
  props: EntitySocialLinksSectionProps,
): JSX.Element {
  const { entityId } = props
  const query = useQuery({
    queryKey: ['entity-social-links', entityId],
    queryFn: () => fetchEntitySocialLinksForAdmin(entityId),
    enabled: /^\d+$/.test(entityId),
  })
  const result = query.data

  return (
    <section
      className="mt-10"
      data-testid={`entity-social-links-section-${entityId}`}
    >
      <h2 className="mb-3 text-lg font-semibold text-slate-100">
        Social links
      </h2>
      <p className="mb-3 text-xs text-slate-400">
        One link per platform per entity. Pick a platform from the
        allowlist, give it a handle, and optionally a full URL.
      </p>

      {query.isLoading && (
        <div
          data-testid={`entity-social-links-loading-${entityId}`}
          className="text-sm text-slate-400"
        >
          Loading social links…
        </div>
      )}

      {result && result.kind === 'error' && (
        <div
          data-testid={`entity-social-links-error-${entityId}`}
          className="rounded border border-rose-700 bg-rose-950/40 p-3 text-sm text-rose-100"
        >
          {result.message}
        </div>
      )}

      {result && (result.kind === 'ok' || result.kind === 'empty') && (
        <EntitySocialLinksTable
          entityId={entityId}
          rows={result.artifact.body.social_links}
        />
      )}

      {result && (result.kind === 'ok' || result.kind === 'empty') && (
        <AddSocialLinkForm entityId={entityId} />
      )}
    </section>
  )
}

interface EntitySocialLinksTableProps {
  entityId: string
  rows: readonly EntitySocialLinkAdminItem[]
}

function EntitySocialLinksTable(
  props: EntitySocialLinksTableProps,
): JSX.Element {
  const { entityId, rows } = props
  if (rows.length === 0) {
    return (
      <p
        data-testid={`entity-social-links-empty-${entityId}`}
        className="text-sm text-slate-400"
      >
        This entity has no social links recorded.
      </p>
    )
  }
  return (
    <div className="overflow-x-auto">
      <table
        data-testid={`entity-social-links-table-${entityId}`}
        className="min-w-full divide-y divide-slate-700 border border-slate-800 text-xs"
      >
        <thead className="bg-slate-900 text-slate-300">
          <tr>
            <th className="px-3 py-2 text-left font-medium">Platform</th>
            <th className="px-3 py-2 text-left font-medium">Handle</th>
            <th className="px-3 py-2 text-left font-medium">URL</th>
            <th className="px-3 py-2 text-left font-medium">Created</th>
            <th className="px-3 py-2 text-left font-medium">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-800">
          {rows.map((row) => (
            <SocialLinkRow key={row.id} entityId={entityId} row={row} />
          ))}
        </tbody>
      </table>
    </div>
  )
}

type SocialLinkRowMode = 'view' | 'edit' | 'confirm-delete'

interface SocialLinkRowProps {
  entityId: string
  row: EntitySocialLinkAdminItem
}

function SocialLinkRow(props: SocialLinkRowProps): JSX.Element {
  const [mode, setMode] = useState<SocialLinkRowMode>('view')
  if (mode === 'edit') {
    return (
      <EditSocialLinkRow
        entityId={props.entityId}
        row={props.row}
        onSaved={() => setMode('view')}
        onCancel={() => setMode('view')}
      />
    )
  }
  return (
    <ViewSocialLinkRow
      entityId={props.entityId}
      row={props.row}
      mode={mode}
      onEdit={() => setMode('edit')}
      onAskDelete={() => setMode('confirm-delete')}
      onCancelDelete={() => setMode('view')}
      onDeleted={() => setMode('view')}
    />
  )
}

interface ViewSocialLinkRowProps {
  entityId: string
  row: EntitySocialLinkAdminItem
  mode: 'view' | 'confirm-delete'
  onEdit: () => void
  onAskDelete: () => void
  onCancelDelete: () => void
  onDeleted: () => void
}

function ViewSocialLinkRow(props: ViewSocialLinkRowProps): JSX.Element {
  const {
    entityId,
    row,
    mode,
    onEdit,
    onAskDelete,
    onCancelDelete,
    onDeleted,
  } = props
  const queryClient = useQueryClient()
  const [networkError, setNetworkError] = useState<string | null>(null)
  const idempotencyKey = useMemo(
    () => newSocialLinkIdempotencyKey(),
    [row.id, mode],
  )

  const mutation = useMutation<
    EntitySocialLinkMutationFetchResult<EntitySocialLinkDeleteReceipt>,
    unknown,
    void
  >({
    mutationFn: () =>
      deleteEntitySocialLink(row.id, {
        body: { idempotency_key: idempotencyKey },
      }),
    onSuccess: (res) => {
      setNetworkError(null)
      if (res.kind === 'ok') {
        queryClient.invalidateQueries({
          queryKey: ['entity-social-links', entityId],
        })
        onDeleted()
      } else if (res.kind === 'csrf_error') {
        setNetworkError(`CSRF: ${res.reason}`)
      } else if (res.kind === 'not_found') {
        // Treat as already-deleted.
        queryClient.invalidateQueries({
          queryKey: ['entity-social-links', entityId],
        })
        onDeleted()
      } else if (res.kind === 'validation_error') {
        setNetworkError(socialLinkValidationMessage(res.error))
      } else {
        setNetworkError(res.message)
      }
    },
  })

  return (
    <tr
      data-testid={`entity-social-link-row-${row.id}`}
      data-platform={row.platform}
      className="text-slate-200"
    >
      <td className="px-3 py-2 align-top font-medium">{row.platform}</td>
      <td className="px-3 py-2 align-top">{row.handle}</td>
      <td className="px-3 py-2 align-top">
        {row.url ? (
          <a
            href={row.url}
            rel="nofollow noopener"
            target="_blank"
            className="text-fishstank-accent underline-offset-2 hover:underline"
          >
            {row.url}
          </a>
        ) : (
          <span className="text-slate-500">—</span>
        )}
      </td>
      <td className="px-3 py-2 align-top text-slate-400">
        {row.created_at.slice(0, 10)}
      </td>
      <td className="px-3 py-2 align-top">
        {mode === 'view' && (
          <div className="flex gap-2">
            <button
              type="button"
              data-testid={`entity-social-link-edit-button-${row.id}`}
              className="rounded border border-slate-700 px-2 py-1 text-xs text-slate-200"
              onClick={onEdit}
            >
              Edit
            </button>
            <button
              type="button"
              data-testid={`entity-social-link-delete-button-${row.id}`}
              className="rounded border border-rose-700 px-2 py-1 text-xs text-rose-200"
              onClick={onAskDelete}
            >
              Delete
            </button>
          </div>
        )}
        {mode === 'confirm-delete' && (
          <div
            data-testid={`entity-social-link-confirm-delete-${row.id}`}
            className="flex flex-col gap-1"
          >
            <span className="text-xs text-rose-200">Confirm delete?</span>
            <div className="flex gap-2">
              <button
                type="button"
                data-testid={`entity-social-link-confirm-delete-yes-${row.id}`}
                className="rounded border border-rose-700 bg-rose-900/40 px-2 py-1 text-xs text-rose-100"
                onClick={() => mutation.mutate()}
                disabled={mutation.isPending}
              >
                {mutation.isPending ? 'Deleting…' : 'Yes, delete'}
              </button>
              <button
                type="button"
                data-testid={`entity-social-link-confirm-delete-cancel-${row.id}`}
                className="rounded border border-slate-700 px-2 py-1 text-xs text-slate-200"
                onClick={onCancelDelete}
                disabled={mutation.isPending}
              >
                Cancel
              </button>
            </div>
            {networkError && (
              <p
                data-testid={`entity-social-link-row-error-${row.id}`}
                className="mt-1 text-xs text-rose-200"
              >
                {networkError}
              </p>
            )}
          </div>
        )}
      </td>
    </tr>
  )
}

interface EditSocialLinkRowProps {
  entityId: string
  row: EntitySocialLinkAdminItem
  onSaved: () => void
  onCancel: () => void
}

function EditSocialLinkRow(props: EditSocialLinkRowProps): JSX.Element {
  const { entityId, row, onSaved, onCancel } = props
  const queryClient = useQueryClient()
  // The DB column is free-form TEXT; if the prior platform is outside
  // the allowlist, default the editor to it (so the operator sees the
  // current value) but the <select> won't have it as an option. The
  // operator must pick an allowlisted value to save.
  const initialPlatform: EntitySocialLinkPlatform =
    (ENTITY_SOCIAL_LINK_PLATFORMS as readonly string[]).includes(row.platform)
      ? (row.platform as EntitySocialLinkPlatform)
      : ENTITY_SOCIAL_LINK_PLATFORMS[0]
  const [platform, setPlatform] =
    useState<EntitySocialLinkPlatform>(initialPlatform)
  const [handle, setHandle] = useState<string>(row.handle)
  const [url, setUrl] = useState<string>(row.url ?? '')
  const [validationError, setValidationError] =
    useState<EntitySocialLinkValidationError | null>(null)
  const [networkError, setNetworkError] = useState<string | null>(null)
  const idempotencyKey = useMemo(() => newSocialLinkIdempotencyKey(), [row.id])

  const mutation = useMutation<
    EntitySocialLinkMutationFetchResult<EntitySocialLinkMutationReceipt>,
    unknown,
    EntitySocialLinkEditRequest
  >({
    mutationFn: (body) => patchEntitySocialLink(row.id, { body }),
    onSuccess: (res) => {
      setValidationError(null)
      setNetworkError(null)
      if (res.kind === 'ok') {
        queryClient.invalidateQueries({
          queryKey: ['entity-social-links', entityId],
        })
        onSaved()
      } else if (res.kind === 'validation_error') {
        setValidationError(res.error)
      } else if (res.kind === 'csrf_error') {
        setNetworkError(`CSRF: ${res.reason}`)
      } else if (res.kind === 'not_found') {
        setNetworkError('This social link no longer exists.')
      } else {
        setNetworkError(res.message)
      }
    },
  })

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault()
    const body: EntitySocialLinkEditRequest = {
      idempotency_key: idempotencyKey,
    }
    if (platform !== row.platform) body.platform = platform
    if (handle !== row.handle) body.handle = handle
    const newUrl = url.trim() === '' ? null : url.trim()
    if (newUrl !== row.url) body.url = newUrl
    const editableKeys = Object.keys(body).filter(
      (k) => k !== 'idempotency_key',
    )
    if (editableKeys.length === 0) {
      setValidationError({ code: 'no_editable_fields' })
      return
    }
    mutation.mutate(body)
  }

  return (
    <tr
      data-testid={`entity-social-link-edit-row-${row.id}`}
      data-platform={row.platform}
    >
      <td className="px-3 py-2">
        <select
          data-testid={`entity-social-link-edit-platform-${row.id}`}
          className="w-full rounded border border-slate-700 bg-slate-900 px-2 py-1 text-xs text-slate-100"
          value={platform}
          onChange={(e) =>
            setPlatform(e.target.value as EntitySocialLinkPlatform)
          }
          disabled={mutation.isPending}
        >
          {ENTITY_SOCIAL_LINK_PLATFORMS.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
      </td>
      <td className="px-3 py-2">
        <input
          data-testid={`entity-social-link-edit-handle-${row.id}`}
          type="text"
          className="w-full rounded border border-slate-700 bg-slate-900 px-2 py-1 text-xs text-slate-100"
          value={handle}
          onChange={(e) => setHandle(e.target.value)}
          maxLength={200}
          required
          disabled={mutation.isPending}
        />
      </td>
      <td className="px-3 py-2">
        <input
          data-testid={`entity-social-link-edit-url-${row.id}`}
          type="text"
          className="w-full rounded border border-slate-700 bg-slate-900 px-2 py-1 text-xs text-slate-100"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          maxLength={2048}
          placeholder="optional"
          disabled={mutation.isPending}
        />
      </td>
      <td className="px-3 py-2 text-slate-500">{row.created_at.slice(0, 10)}</td>
      <td className="px-3 py-2 align-top">
        {validationError && (
          <p
            data-testid={`entity-social-link-edit-error-${row.id}`}
            className="mb-1 text-xs text-rose-200"
          >
            {socialLinkValidationMessage(validationError)}
          </p>
        )}
        {networkError && (
          <p
            data-testid={`entity-social-link-edit-network-error-${row.id}`}
            className="mb-1 text-xs text-rose-200"
          >
            {networkError}
          </p>
        )}
        <form
          data-testid={`entity-social-link-edit-form-${row.id}`}
          className="flex gap-2"
          onSubmit={handleSubmit}
        >
          <button
            type="submit"
            data-testid={`entity-social-link-edit-submit-${row.id}`}
            className="rounded border border-fishstank-accent px-2 py-1 text-xs text-fishstank-accent"
            disabled={mutation.isPending}
          >
            {mutation.isPending ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            data-testid={`entity-social-link-edit-cancel-${row.id}`}
            className="rounded border border-slate-700 px-2 py-1 text-xs text-slate-200"
            onClick={onCancel}
            disabled={mutation.isPending}
          >
            Cancel
          </button>
        </form>
      </td>
    </tr>
  )
}

interface AddSocialLinkFormProps {
  entityId: string
}

function AddSocialLinkForm(props: AddSocialLinkFormProps): JSX.Element {
  const { entityId } = props
  const queryClient = useQueryClient()
  const [platform, setPlatform] = useState<EntitySocialLinkPlatform>(
    ENTITY_SOCIAL_LINK_PLATFORMS[0],
  )
  const [handle, setHandle] = useState<string>('')
  const [url, setUrl] = useState<string>('')
  const [validationError, setValidationError] =
    useState<EntitySocialLinkValidationError | null>(null)
  const [networkError, setNetworkError] = useState<string | null>(null)
  const [recentlyAdded, setRecentlyAdded] = useState<string | null>(null)
  const [idempotencyKey, setIdempotencyKey] = useState<string>(() =>
    newSocialLinkIdempotencyKey(),
  )

  const mutation = useMutation<
    EntitySocialLinkMutationFetchResult<EntitySocialLinkMutationReceipt>,
    unknown,
    EntitySocialLinkCreateRequest
  >({
    mutationFn: (body) => postEntitySocialLink({ body }),
    onSuccess: (res) => {
      setValidationError(null)
      setNetworkError(null)
      if (res.kind === 'ok') {
        setRecentlyAdded(
          `${res.artifact.body.row.platform} / ${res.artifact.body.row.handle}`,
        )
        queryClient.invalidateQueries({
          queryKey: ['entity-social-links', entityId],
        })
        // Clear form + rotate idempotency key for the next add.
        setHandle('')
        setUrl('')
        // Leave platform on its current pick — operator may add
        // multiple links in a row, picking different platforms each
        // time; clearing it would force them to re-pick from default.
        setIdempotencyKey(newSocialLinkIdempotencyKey())
      } else if (res.kind === 'validation_error') {
        setValidationError(res.error)
      } else if (res.kind === 'csrf_error') {
        setNetworkError(`CSRF: ${res.reason}`)
      } else if (res.kind === 'not_found') {
        setNetworkError('This entity no longer exists.')
      } else {
        setNetworkError(res.message)
      }
    },
  })

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault()
    const body: EntitySocialLinkCreateRequest = {
      entity_id: entityId,
      platform,
      handle,
      idempotency_key: idempotencyKey,
    }
    const trimmedUrl = url.trim()
    if (trimmedUrl.length > 0) body.url = trimmedUrl
    mutation.mutate(body)
  }

  return (
    <form
      data-testid={`entity-social-link-add-form-${entityId}`}
      className="mt-3 flex flex-col gap-2 rounded border border-slate-800 p-3 text-xs"
      onSubmit={handleSubmit}
    >
      <div className="flex flex-wrap items-end gap-2">
        <label className="flex flex-col">
          <span className="mb-1 text-slate-300">Platform</span>
          <select
            data-testid={`entity-social-link-add-platform-${entityId}`}
            className="rounded border border-slate-700 bg-slate-900 px-2 py-1 text-slate-100"
            value={platform}
            onChange={(e) =>
              setPlatform(e.target.value as EntitySocialLinkPlatform)
            }
            disabled={mutation.isPending}
          >
            {ENTITY_SOCIAL_LINK_PLATFORMS.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col">
          <span className="mb-1 text-slate-300">Handle</span>
          <input
            data-testid={`entity-social-link-add-handle-${entityId}`}
            type="text"
            className="rounded border border-slate-700 bg-slate-900 px-2 py-1 text-slate-100"
            value={handle}
            onChange={(e) => setHandle(e.target.value)}
            maxLength={200}
            required
            placeholder="@username"
            disabled={mutation.isPending}
          />
        </label>
        <label className="flex flex-col flex-1 min-w-[16rem]">
          <span className="mb-1 text-slate-300">URL (optional)</span>
          <input
            data-testid={`entity-social-link-add-url-${entityId}`}
            type="text"
            className="rounded border border-slate-700 bg-slate-900 px-2 py-1 text-slate-100"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            maxLength={2048}
            placeholder="https://"
            disabled={mutation.isPending}
          />
        </label>
        <button
          type="submit"
          data-testid={`entity-social-link-add-submit-${entityId}`}
          className="rounded border border-fishstank-accent px-3 py-1 text-fishstank-accent"
          disabled={mutation.isPending}
        >
          {mutation.isPending ? 'Adding…' : 'Add link'}
        </button>
      </div>

      {validationError && (
        <p
          data-testid={`entity-social-link-add-error-${entityId}`}
          className="text-rose-200"
        >
          {socialLinkValidationMessage(validationError)}
        </p>
      )}
      {networkError && (
        <p
          data-testid={`entity-social-link-add-network-error-${entityId}`}
          className="text-rose-200"
        >
          {networkError}
        </p>
      )}
      {recentlyAdded && (
        <p
          data-testid={`entity-social-link-add-receipt-${entityId}`}
          className="text-emerald-200"
        >
          Added <strong>{recentlyAdded}</strong>.
        </p>
      )}
    </form>
  )
}
